/**
 * llm-prices.com Data Source - v13 Section 6.10
 *
 * Primary source for LLM pricing data.
 * Community-maintained, CORS-friendly, no auth required.
 *
 * @see https://www.llm-prices.com/
 * @see https://github.com/simonw/llm-prices
 */
import { LLM_PRICES_URL } from '../defaults';
/**
 * Map vendor names from llm-prices.com to our Provider type
 */
const VENDOR_TO_PROVIDER = {
    openai: 'openai',
    anthropic: 'anthropic',
    google: 'google',
    groq: 'groq',
    deepinfra: 'deepinfra',
    together: 'deepinfra', // Map Together AI to deepinfra for simplicity
    mistral: 'deepinfra',
    cohere: 'deepinfra',
};
/**
 * llm-prices.com doesn't provide context window data
 * Return null - should be merged from other sources
 */
const getContextWindow = () => {
    return null;
};
/**
 * llm-prices.com doesn't provide max completion tokens
 * Return null - should be merged from other sources
 */
const getMaxCompletionTokens = () => {
    return null;
};
/**
 * llm-prices.com doesn't provide reasoning model info
 * Return null - should be merged from other sources
 */
const isReasoningModel = () => {
    return null;
};
/**
 * Determine if provider has zero data retention
 * Returns null if unknown
 */
const isZeroDataRetention = (provider) => {
    // Known ZDR providers - this is structural knowledge, not model data
    if (provider === 'groq' || provider === 'deepinfra' || provider === 'ollama') {
        return true;
    }
    return null;
};
/**
 * Create display name from model ID using generic cleanup
 * No hardcoded mapping - just transform the ID into readable format
 */
const createDisplayName = (modelId) => {
    // Generate from model ID - generic cleanup, no mapping
    const cleaned = modelId
        .replace(/^(openai\/|anthropic\/|google\/|meta-llama\/)/, '')
        .replace(/-/g, ' ')
        .replace(/(\d+)b/gi, '$1B')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    return cleaned;
};
/**
 * Empty tier configuration - tiers should be configured at application level
 */
const EMPTY_TIERS = {
    fast: {
        primaryModel: '',
        fallbackModels: [],
        avgCostPer1k: 0,
        description: 'Fast models - configure at application level',
    },
    standard: {
        primaryModel: '',
        fallbackModels: [],
        avgCostPer1k: 0,
        description: 'Standard models - configure at application level',
    },
    quality: {
        primaryModel: '',
        fallbackModels: [],
        avgCostPer1k: 0,
        description: 'Quality models - configure at application level',
    },
    local: {
        primaryModel: '',
        fallbackModels: [],
        avgCostPer1k: 0,
        description: 'Local models - configure at application level',
    },
};
/**
 * Transform llm-prices.com response to our config format
 * Uses only API-provided data - returns null for missing fields
 */
const transformLLMPricesResponse = (response) => {
    const models = {};
    const fallbackModels = [];
    for (const price of response.prices) {
        const provider = VENDOR_TO_PROVIDER[price.vendor];
        if (!provider)
            continue; // Skip unknown vendors
        const modelId = price.id;
        const displayName = createDisplayName(modelId);
        // llm-prices.com provides pricing, but not context/completion limits or reasoning info
        models[modelId] = {
            id: modelId,
            provider,
            displayName,
            contextWindow: getContextWindow(),
            maxCompletionTokens: getMaxCompletionTokens(),
            pricing: {
                inputPer1M: price.input,
                outputPer1M: price.output,
                cachedInputPer1M: price.input_cached ?? undefined,
            },
            capabilities: [], // Not provided by llm-prices.com
            zeroDataRetention: isZeroDataRetention(provider),
            isReasoningModel: isReasoningModel(),
        };
    }
    // Create fallback models from top models per provider
    const providerModels = {};
    for (const [modelId, metadata] of Object.entries(models)) {
        if (!providerModels[metadata.provider]) {
            providerModels[metadata.provider] = [];
        }
        providerModels[metadata.provider].push(modelId);
    }
    // Pick first model from each provider for fallbacks
    for (const [provider, modelIds] of Object.entries(providerModels)) {
        if (modelIds.length > 0) {
            const modelId = modelIds[0];
            fallbackModels.push({
                provider,
                model: modelId,
                displayName: models[modelId].displayName,
            });
        }
    }
    return {
        timestamp: new Date(response.updated_at).getTime(),
        source: 'llm-prices.com',
        models,
        tiers: EMPTY_TIERS,
        fallbackModels,
    };
};
/**
 * Fetch configuration from llm-prices.com
 *
 * @throws Error if fetch fails
 */
export const fetchFromLLMPrices = async () => {
    const response = await fetch(LLM_PRICES_URL, {
        headers: {
            Accept: 'application/json',
        },
    });
    if (!response.ok) {
        throw new Error(`llm-prices.com returned ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    if (!data.prices || !Array.isArray(data.prices)) {
        throw new Error('Invalid response format from llm-prices.com');
    }
    return transformLLMPricesResponse(data);
};
